import { m } from "framer-motion";
import React, { useEffect, useMemo, useRef } from "react";
import Link from "next/link";
import FBSVG from "./FBSVG";
import InstaSVG from "./InstaSVG";
import YTSVG from "./YTSVG";
import TwitterSVG from "./TwitterSVG";
import styles from "./USFooter.module.scss";
import { peekCachedCountry } from "@/components/us-website/LocationWrapper";
import {
  MARKET_INSIGHTS_ENABLED,
  SOCIAL_LINKS,
} from "../../../constastants/Constant";
import { useSelector } from "react-redux";
import { LinkedIn } from "@mui/icons-material";
import { usePathname } from "next/navigation";
import { getIntlUrl as getIntlUrlUtil } from "@/utils/Common";

const slugify = (industryName, industryId) => {
  return industryName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric chars with hyphen
    .replace(/^-+|-+$/g, "") // Remove leading/trailing hyphens
    .concat(`-${industryId}`); // Append ID for uniqueness
};

//Industries
const industries = [
  {
    id: 1,
    name: "Water Treatment & Mining",
    slug: slugify("Water Treatment & Mining", 1),
  },
  {
    id: 3,
    name: "Personal Care",
    slug: slugify("Personal Care", 3),
  },
  {
    id: 2,
    name: "Oil & Gas",
    slug: slugify("Oil & Gas", 2),
  },
  {
    id: 5,
    name: "Flavours & Fragrances",
    slug: slugify("Flavours & Fragrances", 5),
  },
  {
    id: 7,
    name: "Chemical Intermediates",
    slug: slugify("Chemical Intermediates", 7),
  },
  {
    id: 4,
    name: "Food & Nutrition",
    slug: slugify("Food & Nutrition", 4),
  },
  {
    id: 6,
    name: "Home Care & Industrial Cleaning",
    slug: slugify("Home Care & Industrial Cleaning", 6),
  },
  {
    id: 8,
    name: "CASE",
    slug: slugify("CASE", 8),
  },
];

//Applications
const applications = [
  {
    name: "Plasticizers",
    url: "/product?category=application&subcategory=plasticizers",
    isProduct: "Application",
  },
  {
    name: "Emulsifier",
    url: "/product?category=application&subcategory=emulsifier",
    isProduct: "Application",
  },
  {
    name: "Mint Chemistry",
    url: "/product?category=application&subcategory=Mint+Chemistry",
    isProduct: "Application",
  },
  {
    name: "UV Absorber",
    url: "/product?category=application&subcategory=Uv+Absorber",
    isProduct: "Application",
  },
  {
    name: "Bleaching Agent",
    url: "/product?category=application&subcategory=Bleaching+Agent",
    isProduct: "Application",
  },
  {
    name: "Antimicrobial",
    url: "/product?category=application&subcategory=Antimicrobial",
    isProduct: "Application",
  },
  {
    name: "Chelating agent",
    url: "/product?category=application&subcategory=Chelating+Agent",
    isProduct: "Application",
  },
  {
    name: "Coagulant",
    url: "/product?category=application&subcategory=Coagulant",
    isProduct: "Application",
  },
];

//Our Services
const ourServices = [
  {
    id: 1,
    name: "Our Technology",
    url: "/elchemy-tech",
  },
  {
    id: 2,
    name: "Custom Manufacturing",
    url: "/quality-custom-chemical-manufacturing/",
  },
  {
    id: 3,
    name: "Optimizing Your Tail Spend",
    url: "/tail-spend-management",
  },
  {
    id: 4,
    name: "Request a Sample",
    url: "/request-a-sample",
  },
];

//Insights
const insights = [
  { id: 1, name: "Events", url: "/events" },
  { id: 2, name: "Blogs", url: "/blogs" },
  { id: 3, name: "FAQs", url: "/faqs" },
  // { id: 4, name: "Market Insights", url: "/market-insights" },
];

//Company
const baseCompany = [
  { id: 1, name: "About Us", url: "/about-us" },
  {
    id: 2,
    name: "Sustainability",
    url: "/sustainability-elchemy",
  },
];
const usCompanyExtras = [
  { id: 3, name: "Market Insights", url: "/market-insights" },
  { id: 4, name: "Careers", url: "/careers" },
].filter((item) => MARKET_INSIGHTS_ENABLED || item.name !== "Market Insights");

function USFooter() {
  const { country } = useSelector((state) => state.userLocation);
  const pathname = usePathname();
  const isIntl = pathname?.endsWith("/intl");
  const getIntlUrl = (url) => getIntlUrlUtil(url, isIntl);
  const company =
    country !== "US" ? [...baseCompany, ...usCompanyExtras] : baseCompany;

  const [cachedLocation, setCachedLocation] = React.useState(null);
  const CL = useMemo(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("location") || null;
    }
  }, []);
  const userCountryRef = useRef(null);

  useEffect(() => {
    // Initialize ref on client side
    userCountryRef.current = peekCachedCountry();

    const interval = setInterval(() => {
      const currentValue = peekCachedCountry();
      if (currentValue !== userCountryRef.current) {
        userCountryRef.current = currentValue;
        setCachedLocation(CL || currentValue);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [CL]);
  return (
    <footer className={styles.usFooter}>
      <div className={styles["footer-lists-row"]}>
        {/* Industries */}
        <div className={styles["footer-list"]}>
          <div className={styles["footer-heading"]}>Industries</div>
          {industries.map((item) => (
            <Link
              key={item.id}
              href={getIntlUrl(`/industry/${item.slug}`)}
              className={styles["footer-link"]}
            >
              {item.name}
            </Link>
          ))}
        </div>
        {/* Applications */}
        <div className={styles["footer-list"]}>
          <div className={styles["footer-heading"]}>Applications</div>
          {applications.map((item, idx) => (
            <Link
              key={idx}
              href={getIntlUrl(item.url)}
              className={styles["footer-link"]}
            >
              {item.name}
            </Link>
          ))}
        </div>
        {/* Our Services */}
        <div className={styles["footer-list"]}>
          <div className={styles["footer-heading"]}>Our Services</div>
          {ourServices.map((item) => (
            <Link
              key={item.id}
              href={getIntlUrl(item.url)}
              className={styles["footer-link"]}
            >
              {item.name}
            </Link>
          ))}
        </div>
        {/* Insights */}
        <div className={styles["footer-list"]}>
          <div className={styles["footer-heading"]}>Insights</div>
          {insights.map((item) => (
            <Link
              key={item.id}
              href={getIntlUrl(item.url)}
              className={styles["footer-link"]}
            >
              {item.name}
            </Link>
          ))}
        </div>
        {/* Company */}
        <div className={styles["footer-list"]}>
          <div className={styles["footer-heading"]}>Company</div>
          {company.map((item) => (
            <Link
              key={item.id}
              href={getIntlUrl(item.url)}
              className={styles["footer-link"]}
            >
              {item.name}
            </Link>
          ))}
          {/* Quick Links (below Company, same column) */}
          <div
            className={styles["footer-heading"]}
            style={{ marginTop: "2rem" }}
          >
            Quick Links
          </div>
          <Link
            href={getIntlUrl("/privacy-policy")}
            className={styles["footer-link"]}
          >
            Privacy Policy
          </Link>
          <Link
            href={getIntlUrl("/terms-and-conditions")}
            className={styles["footer-link"]}
          >
            Terms Of Services
          </Link>
          <Link
            href={getIntlUrl("/statutory-disclosures")}
            className={styles["footer-link"]}
          >
            Statutory Disclosures
          </Link>
          <Link href={getIntlUrl("/sitemap")} className={styles["footer-link"]}>
            Sitemap
          </Link>
          <a
            href="https://elchemy-website.s3.ap-south-1.amazonaws.com/Elchemy_Supplier+Code+of+Conduct.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles["footer-link"]}
          >
            Supplier Code of Conduct
          </a>
        </div>
      </div>
      <div className={styles["footer-bottom-row"]}>
        <div className={styles["footer-icons"]}>
          {SOCIAL_LINKS.filter((link) =>
            [
              "facebook",
              "instagram",
              "youtube",
              "twitter",
              "linkedin",
            ].includes(link.type),
          ).map((link) => (
            <a
              key={link.type}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={
                link.type.charAt(0).toUpperCase() + link.type.slice(1)
              }
            >
              {link.type === "linkedin" && (
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    border: "1px solid white",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    style={{ fill: "white" }}
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </div>
              )}
              {link.type === "facebook" && <FBSVG />}
              {link.type === "instagram" && <InstaSVG />}
              {link.type === "youtube" && <YTSVG />}
              {link.type === "twitter" && <TwitterSVG />}
            </a>
          ))}
        </div>
        <div className={styles.copyright}>
          {cachedLocation == "US" ? (
            <>
              Copyright {`@${new Date().getFullYear()}`} Elchemy, Inc. | All
              Rights Reserved
            </>
          ) : (
            <>
              Copyright {`@${new Date().getFullYear()}`} Elchemy (Bizinbiz
              Technologies Private Limited) | All Rights Reserved
            </>
          )}
        </div>
      </div>
    </footer>
  );
}

export default USFooter;
